import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { createApiClient } from "../api/client";
import { 
  BarChart3, Calendar, Download, AlertCircle, 
  Loader2, Filter, Layers, Check
} from "lucide-react";

export default function SummaryReports() {
  const { token, logout } = useAuth();
  const apiClient = useMemo(() => createApiClient(token, logout), [token, logout]);

  // --- Initial Date Setup ---
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const formatDateStr = (d) => d.toISOString().split('T')[0];

  // --- State Management ---
  const [buyers, setBuyers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  
  const [filters, setFilters] = useState({
    buyerId: "",
    supplierCode: "",
    startDate: formatDateStr(sevenDaysAgo),
    endDate: formatDateStr(today),
    groupBy: "Daily"
  });

  const [reportData, setReportData] = useState([]);
  const [reportTotals, setReportTotals] = useState(null);
  
  const [isLoadingBuyers, setIsLoadingBuyers] = useState(true);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  // 1. Fetch available websites (buyers) on mount
  useEffect(() => {
    const fetchBuyers = async () => {
      setIsLoadingBuyers(true);
      try {
        const response = await apiClient(`/Leads/buyers`);
        if (!response.ok) throw new Error("Failed to load websites.");
        const data = await response.json();
        setBuyers(data);
      } catch (err) {
        console.error(err);
        setError("Unable to load website list.");
        
        // Fallback for development
        if (process.env.NODE_ENV === 'development') {
           setBuyers([
             { buyerID: 1, companyName: "Viva Leads" },
             { buyerID: 2, companyName: "Apex Data" }
           ]);
        }
      } finally {
        setIsLoadingBuyers(false);
      }
    };
    fetchBuyers();
  }, [apiClient]);

  // 2. Fetch campaigns when a website (buyer) is selected
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!filters.buyerId) {
        setCampaigns([]);
        return;
      }
      setIsLoadingCampaigns(true);
      try {
        const response = await apiClient(`/Campaigns/by-buyer/${filters.buyerId}`);
        if (!response.ok) throw new Error("Failed to load campaigns.");
        const data = await response.json();
        setCampaigns(data);
      } catch (err) {
        console.error(err);
        setError("Unable to load campaign list.");
        
        // Fallback for development
        if (process.env.NODE_ENV === 'development') {
           setCampaigns([
             { campaignName: "FB Ads", supplierCode: "VIVA-FB" },
             { campaignName: "Direct Inbound", supplierCode: "VIVA-DIR" }
           ]);
        }
      } finally {
        setIsLoadingCampaigns(false);
      }
    };
    fetchCampaigns();
  }, [apiClient, filters.buyerId]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === "buyerId") {
      // Reset the selected campaign when the website changes
      setFilters(prev => ({ ...prev, buyerId: value, supplierCode: "" }));
    } else {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleGenerateReport = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!filters.supplierCode) {
      setError("Please select a campaign to generate a report.");
      return;
    }

    setIsLoadingReport(true);
    setIsSuccess(false);
    setError("");
    setReportData([]);
    setReportTotals(null);

    try {
      const params = new URLSearchParams({
        supplierCode: filters.supplierCode,
        startDate: filters.startDate,
        endDate: filters.endDate,
        groupBy: filters.groupBy
      });

      const response = await apiClient(`/reports/summary?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch report data.");
      
      const data = await response.json();
      setReportData(data.data || []);
      setReportTotals(data.totals || null);
      
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 1500);
    } catch (err) {
      console.warn("Report Fetch Error", err);
      setError(err.message || "An error occurred while generating the report.");
      
      // Fallback mock data for development testing
      if (process.env.NODE_ENV === 'development') {
         setReportData([
           { period: "2023-10-05", totalAttempts: 100, pingRejects: 10, soldLeads: 45, revenue: 450.00 },
           { period: "2023-10-06", totalAttempts: 120, pingRejects: 15, soldLeads: 50, revenue: 500.00 },
           { period: "2023-10-07", totalAttempts: 90, pingRejects: 5, soldLeads: 40, revenue: 400.00 }
         ]);
         setReportTotals(
           { period: "TOTAL", totalAttempts: 310, pingRejects: 30, soldLeads: 135, revenue: 1350.00 }
         );
         setIsSuccess(true);
         setTimeout(() => setIsSuccess(false), 1500);
         setError("");
      }
    } finally {
      setIsLoadingReport(false);
    }
  }, [filters, apiClient]);

  const handleExportCSV = () => {
    if (reportData.length === 0) return;
    
    const headers = ["Period", "Total Attempts", "Ping Rejects", "Sold Leads", "Revenue"];
    const csvRows = [headers.join(",")];
    
    // Add data rows
    reportData.forEach((row) => {
      const values = [
        row.period,
        row.totalAttempts,
        row.pingRejects,
        row.soldLeads,
        row.revenue
      ];
      csvRows.push(values.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    });
    
    // Add totals row
    if (reportTotals) {
       const totalValues = [
          "TOTAL",
          reportTotals.totalAttempts,
          reportTotals.pingRejects,
          reportTotals.soldLeads,
          reportTotals.revenue
       ];
       csvRows.push(totalValues.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    }

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `summary_report_${filters.startDate}_to_${filters.endDate}.csv`;
    link.click();
  };

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, staggerChildren: 0.05 } 
    }
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  // Custom Floating Input Component
  const FloatingInput = ({ name, label, type = "text", icon: Icon }) => (
    <div className="relative group w-full">
      <input
        type={type}
        name={name}
        id={name}
        placeholder=" "
        value={filters[name]}
        onChange={handleFilterChange}
        className="block w-full px-3.5 pb-2 pt-6 border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[13px] text-text-primary shadow-small peer appearance-none"
      />
      <label
        htmlFor={name}
        className="absolute text-[13px] text-text-secondary duration-300 transform -translate-y-4 scale-75 top-4 z-10 origin-[0] start-3.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-primary inline-flex items-center pointer-events-none font-semibold"
      >
        {Icon && <Icon className="w-3.5 h-3.5 me-2 transition-colors" />}
        {label}
      </label>
    </div>
  );

  return (
    <div className="max-w-[1200px] mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 text-[14px]">
      
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-[24px] font-bold text-text-primary tracking-tight">Summary Reports</h1>
        <p className="text-text-secondary font-medium mt-1">Generate aggregated performance metrics for your campaigns.</p>
      </div>

      {/* Filter Engine */}
      <div className="bg-bg-card rounded-card shadow-card border border-border-light p-6">
        <form onSubmit={handleGenerateReport} className="space-y-6">
          <div className="flex flex-col xl:flex-row gap-5 items-end">
            
            {/* Dropdowns & Dates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 flex-1 w-full">
              
              <div className="space-y-1.5 relative">
                <label className="text-[11px] font-bold text-text-muted uppercase tracking-wider ml-1">Website</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
                    <Filter className="w-4 h-4" />
                  </div>
                  <select
                    name="buyerId"
                    className="block w-full pl-10 pr-3.5 py-2.5 border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[14px] font-medium text-text-primary shadow-small appearance-none"
                    value={filters.buyerId}
                    onChange={handleFilterChange}
                    required
                  >
                    <option value="" disabled>{isLoadingBuyers ? "Loading..." : "Select Website..."}</option>
                    {buyers.map(b => (
                      <option key={b.buyerID} value={b.buyerID}>{b.companyName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1.5 relative">
                <label className="text-[11px] font-bold text-text-muted uppercase tracking-wider ml-1">Campaign</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
                    <Filter className="w-4 h-4" />
                  </div>
                  <select
                    name="supplierCode"
                    className="block w-full pl-10 pr-3.5 py-2.5 border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[14px] font-medium text-text-primary shadow-small appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                    value={filters.supplierCode}
                    onChange={handleFilterChange}
                    required
                    disabled={!filters.buyerId || isLoadingCampaigns}
                  >
                    <option value="" disabled>{isLoadingCampaigns ? "Loading..." : "Select Campaign..."}</option>
                    {campaigns.map(c => (
                      <option key={c.supplierCode} value={c.supplierCode}>{c.campaignName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1.5 relative pt-[21px]">
                 <FloatingInput name="startDate" label="Start Date" type="date" icon={Calendar} />
              </div>
              
              <div className="space-y-1.5 relative pt-[21px]">
                 <FloatingInput name="endDate" label="End Date" type="date" icon={Calendar} />
              </div>

              <div className="space-y-1.5 relative">
                <label className="text-[11px] font-bold text-text-muted uppercase tracking-wider ml-1">Group By</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
                    <Layers className="w-4 h-4" />
                  </div>
                  <select
                    name="groupBy"
                    className="block w-full pl-10 pr-3.5 py-2.5 border border-border-light rounded-input bg-bg-soft focus:ring-2 focus:ring-primary-light focus:border-primary transition-all outline-none text-[14px] font-medium text-text-primary shadow-small appearance-none"
                    value={filters.groupBy}
                    onChange={handleFilterChange}
                  >
                    <option value="Daily">Daily</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 w-full xl:w-auto h-[44px]">
              <motion.button
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isLoadingReport || !filters.supplierCode}
                animate={{ backgroundColor: isSuccess ? "var(--color-status-complete)" : "var(--color-primary)" }}
                className="flex-1 xl:flex-none inline-flex items-center justify-center px-6 py-2.5 bg-primary text-white font-bold rounded-button shadow-card transition-all disabled:opacity-70 min-w-[160px] h-full"
              >
                <AnimatePresence mode="wait">
                  {isSuccess ? (
                    <motion.div key="success" initial={{ scale: 0 }} animate={{ scale: 1 }}><Check className="w-5 h-5" /></motion.div>
                  ) : isLoadingReport ? (
                    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />Generating
                    </motion.div>
                  ) : (
                    <motion.div key="text" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 mr-2" />Generate Report
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
              
              <motion.button
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={handleExportCSV}
                disabled={reportData.length === 0}
                className="p-2.5 rounded-button border border-border-medium bg-bg-card text-text-secondary hover:bg-bg-soft disabled:opacity-50 transition-all shadow-small h-full flex items-center justify-center"
                title="Export CSV"
              >
                <Download className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        </form>
      </div>

      {error && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-bg-warning border border-status-pending rounded-card flex items-start gap-3 shadow-small">
          <AlertCircle className="w-5 h-5 text-feedback-warning flex-shrink-0" />
          <p className="text-[14px] text-text-primary font-medium">{error}</p>
        </motion.div>
      )}

      {/* Data Table Area */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-bg-card shadow-card rounded-card border border-border-light overflow-hidden flex flex-col"
      >
        <div className="overflow-x-auto min-h-[300px]">
          <table className="min-w-full divide-y divide-border-light">
            <thead className="bg-bg-soft/50">
              <tr>
                <th className="px-6 py-4 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Period</th>
                <th className="px-6 py-4 text-right text-[11px] font-bold text-text-muted uppercase tracking-wider">Total Attempts</th>
                <th className="px-6 py-4 text-right text-[11px] font-bold text-text-muted uppercase tracking-wider">Ping Rejects</th>
                <th className="px-6 py-4 text-right text-[11px] font-bold text-text-muted uppercase tracking-wider">Sold Leads</th>
                <th className="px-6 py-4 text-right text-[11px] font-bold text-text-muted uppercase tracking-wider">Revenue</th>
              </tr>
            </thead>
            <AnimatePresence mode="wait">
              <motion.tbody 
                key={reportData.length > 0 ? 'data' : 'empty'}
                className="bg-bg-card divide-y divide-border-light"
              >
                {isLoadingReport ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`skel-${i}`}>
                      {[1, 2, 3, 4, 5].map((j) => (
                        <td key={`skel-c-${j}`} className="px-6 py-4"><div className={`h-4 bg-bg-soft rounded-[4px] animate-pulse ${j === 1 ? 'w-1/2' : 'w-full block ml-auto'}`}></div></td>
                      ))}
                    </tr>
                  ))
                ) : reportData.length > 0 ? (
                  reportData.map((row, idx) => (
                    <motion.tr 
                      key={`row-${idx}`} 
                      variants={rowVariants} 
                      className="hover:bg-bg-soft/40 transition-colors"
                    >
                      <td className="px-6 py-3 whitespace-nowrap text-[13px] font-bold text-text-primary">
                        {row.period}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-[13px] font-medium text-text-secondary text-right">
                        {row.totalAttempts.toLocaleString()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-[13px] font-medium text-feedback-warning text-right">
                        {row.pingRejects.toLocaleString()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-[13px] font-bold text-feedback-success text-right">
                        {row.soldLeads.toLocaleString()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-[14px] font-bold text-text-primary text-right">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.revenue)}
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <motion.tr variants={rowVariants}>
                    <td colSpan={5} className="px-6 py-24 text-center">
                      <div className="flex flex-col items-center justify-center text-text-muted">
                        <BarChart3 className="w-12 h-12 mb-4 opacity-20" />
                        <h3 className="text-[18px] font-bold text-text-primary tracking-tight">No Report Generated</h3>
                        <p className="text-[14px] text-text-secondary font-medium mt-1 max-w-sm">Select a campaign and date range above to generate your summary report.</p>
                      </div>
                    </td>
                  </motion.tr>
                )}
              </motion.tbody>
            </AnimatePresence>
            
            {/* Grand Totals Footer */}
            {reportTotals && !isLoadingReport && (
              <tfoot className="bg-bg-soft border-t-2 border-border-medium">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-[13px] font-black text-text-primary uppercase tracking-wider">
                    {reportTotals.period}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-[14px] font-black text-text-primary text-right">
                    {reportTotals.totalAttempts.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-[14px] font-black text-feedback-warning text-right">
                    {reportTotals.pingRejects.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-[14px] font-black text-feedback-success text-right">
                    {reportTotals.soldLeads.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-[15px] font-black text-primary text-right">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(reportTotals.revenue)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </motion.div>
    </div>
  );
}
